import { type ReactNode } from 'react'
import { ListFilter } from 'lucide-react'
import { Menu } from '@highlight/ui'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

export interface ViewFilterOption<T extends string> {
  value: T
  label: string
  /** Right-aligned count, when the view has one worth showing. */
  count?: number
}

interface ViewFilterMenuProps<T extends string> {
  value: T
  options: Array<ViewFilterOption<T>>
  onChange: (value: T) => void
  /** Names what is being filtered, e.g. "history" → "Filter history: All". */
  subject: string
  /** Extra items below a separator — controls that belong to the list, not the filter. */
  children?: ReactNode
}

/**
 * The filter chip every full-list View wears in its header: a `list-filter`
 * glyph and the active filter's label, opening the full set as a menu (Figma
 * `6790:6560`).
 *
 * One component rather than three near-identical menus because the chip is the
 * views' shared vocabulary — History, Outbox and Reminders each filter a
 * different list, but the affordance reads the same in all three.
 */
export function ViewFilterMenu<T extends string>({
  value,
  options,
  onChange,
  subject,
  children,
}: ViewFilterMenuProps<T>) {
  const activeLabel = options.find((option) => option.value === value)?.label ?? ''

  // Tracked here rather than per caller so every list's tabs report one event,
  // the Outbox's Drafts/All/Completed/Archived included.
  const handleSelect = (next: T) => {
    AmplitudeRenderer.trackEvent('View Filter', { subject, filter: next, previousFilter: value })
    onChange(next)
  }

  return (
    <Menu>
      <Menu.Trigger
        variant="ghost"
        aria-label={`Filter ${subject}: ${activeLabel}`}
        onClick={(e) => e.stopPropagation()}
        className="text-foreground-secondary gap-1.5 px-1.5 text-xs font-medium"
      >
        <ListFilter />
        <span>{activeLabel}</span>
      </Menu.Trigger>
      <Menu.Content>
        {options.map((option) => (
          <Menu.Item key={option.value} onClick={() => handleSelect(option.value)}>
            <span>{option.label}</span>
            {option.count !== undefined && (
              <span className="text-foreground-secondary type-ui-xs ml-auto tabular-nums">{option.count}</span>
            )}
          </Menu.Item>
        ))}
        {children}
      </Menu.Content>
    </Menu>
  )
}
